export type PullGesturePhase = 'idle' | 'tracking' | 'pulling' | 'armed';

export interface PullGestureState {
  phase: PullGesturePhase;
  startX: number;
  startY: number;
  pullDistance: number;
}

export type PullGestureEvent =
  | { type: 'start'; x: number; y: number; touchCount: number; canStart: boolean }
  | { type: 'move'; x: number; y: number; touchCount: number; isAtBoundary: boolean }
  | { type: 'release' }
  | { type: 'cancel' };

export interface PullGestureOptions {
  directionSlopPx?: number;
  thresholdPx?: number;
  resistance?: number;
  maxPullDistancePx?: number;
}

export interface PullGestureTransition {
  state: PullGestureState;
  shouldPreventDefault: boolean;
  shouldRefresh: boolean;
}

const DEFAULT_DIRECTION_SLOP_PX = 8;
const DEFAULT_THRESHOLD_PX = 60;
const DEFAULT_RESISTANCE = 0.5;
const DEFAULT_MAX_PULL_DISTANCE_PX = 96;

export const createIdlePullGestureState = (): PullGestureState => ({
  phase: 'idle',
  startX: 0,
  startY: 0,
  pullDistance: 0,
});

const idleTransition = (shouldRefresh = false): PullGestureTransition => ({
  state: createIdlePullGestureState(),
  shouldPreventDefault: false,
  shouldRefresh,
});

export const reducePullGesture = (
  state: PullGestureState,
  event: PullGestureEvent,
  options: PullGestureOptions = {},
): PullGestureTransition => {
  const directionSlopPx = options.directionSlopPx ?? DEFAULT_DIRECTION_SLOP_PX;
  const thresholdPx = options.thresholdPx ?? DEFAULT_THRESHOLD_PX;
  const resistance = options.resistance ?? DEFAULT_RESISTANCE;
  const maxPullDistancePx = options.maxPullDistancePx ?? DEFAULT_MAX_PULL_DISTANCE_PX;

  if (event.type === 'cancel') {
    return idleTransition();
  }

  if (event.type === 'release') {
    return idleTransition(state.phase === 'armed');
  }

  if (event.type === 'start') {
    if (!event.canStart || event.touchCount !== 1) {
      return idleTransition();
    }

    return {
      state: {
        phase: 'tracking',
        startX: event.x,
        startY: event.y,
        pullDistance: 0,
      },
      shouldPreventDefault: false,
      shouldRefresh: false,
    };
  }

  if (state.phase === 'idle' || event.touchCount !== 1 || !event.isAtBoundary) {
    return idleTransition();
  }

  const deltaX = event.x - state.startX;
  const deltaY = event.y - state.startY;
  const absoluteX = Math.abs(deltaX);
  const absoluteY = Math.abs(deltaY);

  if (state.phase === 'tracking') {
    if (Math.max(absoluteX, absoluteY) < directionSlopPx) {
      return { state, shouldPreventDefault: false, shouldRefresh: false };
    }

    if (deltaY <= 0 || absoluteX >= absoluteY) {
      return idleTransition();
    }
  }

  if (deltaY <= 0) {
    return idleTransition();
  }

  const pullDistance = Math.min(maxPullDistancePx, deltaY * resistance);

  return {
    state: {
      ...state,
      phase: deltaY >= thresholdPx ? 'armed' : 'pulling',
      pullDistance,
    },
    shouldPreventDefault: true,
    shouldRefresh: false,
  };
};
